/**
 * Gamification for **time-tracker** — domain layer (app-specific).
 *
 * The core (`engine.ts`) is agnostic; here we define WHAT awards rewards in
 * time tracking: logging time blocks and completing focus
 * sessions (pomodoro). "Days worked" streak. **Positive and
 * optional** gamification; private leagues by default.
 *
 * LOCAL-FIRST source: the live `time_entry`/`focus_session` tables (not
 * `activity_recent`, which the server projects). Pure and deterministic reducer
 * → instant feedback, also for guests without sync.
 */
import { counterAtLeast, levelAtLeast, streakAtLeast } from "./badges";
import { applyEvents, type GamificationConfig, initialState, type XpRule } from "./engine";
import { computeStreak, dayKeyUTC } from "./streaks";
import type { GameEvent, PlayerState } from "./types";

/** Streak line: "days with logged activity" (work). */
export const STREAK_WORK = "work";

const MS_PER_HOUR = 3_600_000;

/** XP per time block: 8 base + 1 per logged hour (cap +12). */
const entryXp: XpRule = (e: GameEvent) => 8 + Math.min(12, Math.floor(e.value ?? 0));

/** Time-tracker `GamificationConfig`. */
export const TIME_TRACKER_GAMIFICATION: GamificationConfig = {
  xp: {
    "entry.logged": entryXp,
    "focus.completed": 15,
  },
  streakLines: {
    "entry.logged": [STREAK_WORK],
    "focus.completed": [STREAK_WORK],
  },
  counters: {
    "entry.logged": ["blocks"],
    "focus.completed": ["focus_sessions"],
  },
  badges: [
    { id: "first-block", title: "Primer bloque", when: counterAtLeast("blocks", 1) },
    { id: "ten-blocks", title: "En marcha", when: counterAtLeast("blocks", 10) },
    { id: "fifty-blocks", title: "Cronometrado", when: counterAtLeast("blocks", 50), bonusXp: 25 },
    {
      id: "century-blocks",
      title: "Maratón",
      when: counterAtLeast("blocks", 100),
      bonusXp: 50,
    },
    { id: "first-focus", title: "Primer foco", when: counterAtLeast("focus_sessions", 1) },
    {
      id: "ten-focus",
      title: "Foco profundo",
      when: counterAtLeast("focus_sessions", 10),
      bonusXp: 25,
    },
    {
      id: "week-streak",
      title: "Semana enfocada",
      when: streakAtLeast(STREAK_WORK, 7),
      bonusXp: 40,
    },
    {
      id: "month-streak",
      title: "Mes productivo",
      when: streakAtLeast(STREAK_WORK, 30),
      bonusXp: 100,
    },
    { id: "level-5", title: "Nivel 5", when: levelAtLeast(5) },
    { id: "level-10", title: "Nivel 10", when: levelAtLeast(10) },
  ],
  quests: [
    {
      id: "daily-1",
      title: "Registra un bloque hoy",
      period: "daily",
      goals: [{ metric: "entry.logged", target: 1 }],
      bonusXp: 10,
    },
    {
      id: "weekly-focus-3",
      title: "3 sesiones de foco esta semana",
      period: "weekly",
      goals: [{ metric: "focus.completed", target: 3 }],
      bonusXp: 40,
    },
  ],
};

/** Badge catalog (for rendering badges not yet earned, in gray). */
export const TIME_TRACKER_BADGES = TIME_TRACKER_GAMIFICATION.badges ?? [];

// ── LOCAL-FIRST derivation from `time_entry` / `focus_session` ────────────

/** Minimal `time_entry` row. A "logged" block is one that already has an end time. */
export interface GamifiableTimeEntry {
  start_time: string | null;
  end_time: string | null;
}

/** Minimal `focus_session` row. Counts "completed" sessions. */
export interface GamifiableFocusSession {
  status: string | null;
  start_time: string | null;
  end_time: string | null;
}

/** Hours (>=0) between start and end, or 0 if missing/invalid. */
function hoursBetween(start: string | null, end: string | null): number {
  if (!start || !end) {
    return 0;
  }
  const a = Date.parse(start);
  const b = Date.parse(end);
  if (Number.isNaN(a) || Number.isNaN(b) || b <= a) {
    return 0;
  }
  return (b - a) / MS_PER_HOUR;
}

/**
 * Derives game events from live rows. Each completed block emits
 * `entry.logged` (day = `start_time`, value = hours); each focus session
 * "completed" emits `focus.completed`. Pure and deterministic.
 */
export function chronosRowsToGameEvents(
  entries: readonly GamifiableTimeEntry[],
  sessions: readonly GamifiableFocusSession[] = [],
): GameEvent[] {
  const events: GameEvent[] = [];
  for (const e of entries) {
    if (!e.start_time || !e.end_time) {
      continue; // block still in progress (no end) does not score yet
    }
    const at = Date.parse(e.start_time);
    if (Number.isNaN(at)) {
      continue;
    }
    events.push({
      type: "entry.logged",
      at,
      day: dayKeyUTC(at),
      value: hoursBetween(e.start_time, e.end_time),
    });
  }
  for (const s of sessions) {
    if (s.status !== "completed") {
      continue;
    }
    const stamp = s.start_time ?? s.end_time;
    const at = stamp ? Date.parse(stamp) : Number.NaN;
    if (Number.isNaN(at)) {
      continue;
    }
    events.push({ type: "focus.completed", at, day: dayKeyUTC(at) });
  }
  events.sort((a, b) => a.at - b.at);
  return events;
}

/**
 * Recomputes player state from `time_entry`/`focus_session`
 * (local-first). Re-anchors work streak to real "today". Pure.
 */
export function computePlayerFromChronos(
  entries: readonly GamifiableTimeEntry[],
  sessions: readonly GamifiableFocusSession[] = [],
  todayKey: string = dayKeyUTC(Date.now()),
): { state: PlayerState; events: number } {
  const events = chronosRowsToGameEvents(entries, sessions);
  const { state } = applyEvents(initialState(), events, TIME_TRACKER_GAMIFICATION);
  const active = state.activeDays[STREAK_WORK] ?? [];
  if (active.length > 0) {
    const streak = computeStreak(active, todayKey, { freezes: state.freezes });
    streak.lastDay = active.reduce((max, d) => (d > max ? d : max), active[0] ?? "");
    state.streaks[STREAK_WORK] = streak;
  }
  return { state, events: events.length };
}
